/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import React from 'react';
import { observable, runInAction, decorate, action } from 'mobx';
import { observer } from 'mobx-react';
import { Icon } from 'semantic-ui-react';

import { formatBytes, swallowError } from '@amzn/base-ui/dist/helpers/utils';
import { isStoreError, isStoreLoading, isStoreEmpty, stopHeartbeat } from '@amzn/base-ui/dist/models/BaseStore';
import BasicProgressPlaceholder from '@amzn/base-ui/dist/parts/helpers/BasicProgressPlaceholder';
import ErrorBox from '@amzn/base-ui/dist/parts/helpers/ErrorBox';

const iconStyle = { lineHeight: 1, height: '1em', margin: 0 };

/**
 * Build a nested tree from a flat list of files whose filenames may contain '/'.
 *
 * Returns an array of nodes:
 *   { type: 'folder', name, path, children: [...nodes], totalSize }
 *   { type: 'file', name, path, file (original file object) }
 *
 * Root-level files (no '/') appear as file nodes at the top level.
 */
function buildFileTree(files) {
  const root = { children: {} };

  files.forEach(file => {
    const parts = file.filename.split('/');
    let current = root;

    for (let i = 0; i < parts.length - 1; i++) {
      const folderName = parts[i];
      if (!current.children[folderName]) {
        current.children[folderName] = { children: {} };
      }
      current = current.children[folderName];
    }

    const fileName = parts[parts.length - 1];
    // Use the full filename as key to avoid collisions with folder names
    current.children[`__file__${file.filename}`] = { file, leafName: fileName };
  });

  function toArray(node, parentPath) {
    const result = [];
    const entries = Object.entries(node.children);

    // Separate folders and files, sort each group alphabetically
    const folders = entries.filter(([, v]) => !v.file).sort(([a], [b]) => a.localeCompare(b));
    const fileEntries = entries.filter(([, v]) => v.file).sort(([, a], [, b]) => a.leafName.localeCompare(b.leafName));

    folders.forEach(([name, child]) => {
      const path = parentPath ? `${parentPath}/${name}` : name;
      const children = toArray(child, path);
      let totalSize = 0;
      // Sum sizes recursively
      const sumSize = (nodes) => {
        nodes.forEach(n => {
          if (n.type === 'file') totalSize += n.file.size;
          else if (n.children) sumSize(n.children);
        });
      };
      sumSize(children);
      result.push({ type: 'folder', name, path, children, totalSize, fileCount: countFiles(children) });
    });

    fileEntries.forEach(([, child]) => {
      result.push({
        type: 'file',
        name: child.leafName,
        path: child.file.filename,
        file: child.file,
      });
    });

    return result;
  }

  function countFiles(nodes) {
    let count = 0;
    nodes.forEach(n => {
      if (n.type === 'file') count++;
      else if (n.children) count += countFiles(n.children);
    });
    return count;
  }

  return toArray(root, '');
}

// expected props
// - study
class StudyFilesTable extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.filesStore = props.study.getFilesStore();
      this.collapsedFolders = new Map();
    });
  }

  componentDidMount() {
    swallowError(this.filesStore.load());
    this.filesStore.startHeartbeat();
  }

  componentWillUnmount() {
    stopHeartbeat(this.filesStore);
  }

  toggleFolder = (path) => {
    if (this.collapsedFolders.has(path)) {
      this.collapsedFolders.delete(path);
    } else {
      this.collapsedFolders.set(path, true);
    }
  };

  render() {
    const store = this.filesStore;
    let content;
    if (isStoreError(store)) {
      content = <ErrorBox error={store.error} className="m0" />;
    } else if (isStoreLoading(store)) {
      content = <BasicProgressPlaceholder segmentCount={1} />;
    } else if (isStoreEmpty(store)) {
      content = this.renderEmpty();
    } else {
      content = this.renderTable();
    }

    return content;
  }

  renderTable() {
    const tree = buildFileTree(this.filesStore.files);
    // If there are no folders at all, render the simple flat table
    const hasAnyFolders = tree.some(n => n.type === 'folder');

    return (
      <table className="study-files-table">
        <thead>
          <tr>
            <th>Filename</th>
            <th>Size</th>
            <th>Last Modified</th>
          </tr>
        </thead>
        <tbody>
          {hasAnyFolders
            ? this.renderTreeRows(tree, 0)
            : this.renderFlatRows()
          }
        </tbody>
      </table>
    );
  }

  renderFlatRows() {
    return this.filesStore.files.map(file => (
      <tr key={file.filename}>
        <td>
          <span
            className="study-file-download-link"
            onClick={() => this.filesStore.downloadFile(file.filename)}
          >
            <Icon name="download" style={iconStyle} />
            {file.filename}
          </span>
        </td>
        <td>{formatBytes(file.size)}</td>
        <td>{file.lastModified.toISOString()}</td>
      </tr>
    ));
  }

  renderTreeRows(nodes, depth) {
    const rows = [];

    nodes.forEach(node => {
      if (node.type === 'folder') {
        const isCollapsed = this.collapsedFolders.has(node.path);
        rows.push(
          <tr
            key={`folder-${node.path}`}
            className="study-files-folder-row"
            onClick={() => this.toggleFolder(node.path)}
          >
            <td colSpan="3">
              <div className="study-files-folder" style={{ paddingLeft: `${depth * 20}px` }}>
                <Icon
                  name={isCollapsed ? 'chevron right' : 'chevron down'}
                  style={{ ...iconStyle, fontSize: '0.8em', marginRight: '6px', color: '#9ca3af' }}
                />
                <Icon
                  name={isCollapsed ? 'folder' : 'folder open'}
                  style={{ ...iconStyle, marginRight: '8px', color: 'var(--rmit-blue-50)' }}
                />
                <span className="study-files-folder-name">{node.name}</span>
                <span className="study-files-folder-meta">
                  {node.fileCount} {node.fileCount === 1 ? 'file' : 'files'} -- {formatBytes(node.totalSize)}
                </span>
              </div>
            </td>
          </tr>,
        );

        if (!isCollapsed) {
          rows.push(...this.renderTreeRows(node.children, depth + 1));
        }
      } else {
        rows.push(
          <tr key={`file-${node.path}`}>
            <td>
              <div style={{ paddingLeft: `${depth * 20}px` }}>
                <span
                  className="study-file-download-link"
                  onClick={() => this.filesStore.downloadFile(node.file.filename)}
                >
                  <Icon name="download" style={iconStyle} />
                  {node.name}
                </span>
              </div>
            </td>
            <td>{formatBytes(node.file.size)}</td>
            <td>{node.file.lastModified.toISOString()}</td>
          </tr>,
        );
      }
    });

    return rows;
  }

  renderEmpty() {
    return (
      <div className="study-empty-state" style={{ padding: '24px' }}>
        <Icon name="file outline" />
        <div className="study-empty-state-title">No files</div>
        <div className="study-empty-state-text">No files are uploaded yet for this study</div>
      </div>
    );
  }
}

decorate(StudyFilesTable, {
  filesStore: observable,
  collapsedFolders: observable,
  toggleFolder: action,
});

export default observer(StudyFilesTable);
